import { useState, useEffect } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { toast } from "sonner";
import { Package, TrendingUp } from "lucide-react";
import { Input } from "../components/ui/input";
import GameSounds from "../lib/sounds";
import Paginator from "../components/Paginator";

const TIER_COLORS = { common: "#94A3B8", uncommon: "#10B981", rare: "#3B82F6", epic: "#B026FF", legendary: "#FFD600" };
const TIER_ORDER = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 };

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [resources, setResources] = useState([]);
  const [sellQty, setSellQty] = useState({});
  const [loading, setLoading] = useState("");
  const [sortMode, setSortMode] = useState("price_asc");
  const [page, setPage] = useState(1);
  const PER_PAGE = 24;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get("/inventory");
        setItems(data.items);
        setResources(data.resources);
      } catch {}
    };
    load();
  }, []);

  const getResource = (name) => resources.find((r) => r.name === name);

  const sell = async (name) => {
    const qty = parseInt(sellQty[name]) || 1;
    if (qty <= 0) return toast.error("Enter a valid amount");
    setLoading(name);
    try {
      const { data } = await API.post("/inventory/sell", { resource_name: name, quantity: qty });
      toast.success(`${data.message} (+${data.earned} coins, fee: ${data.fee})`);
      GameSounds.purchase();
      const inv = await API.get("/inventory");
      setItems(inv.data.items);
      setSellQty((p) => ({ ...p, [name]: "" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  // Apply selected sort
  const sortedItems = [...items].sort((a, b) => {
    const ra = getResource(a.resource_name);
    const rb = getResource(b.resource_name);
    const pa = ra?.current_price || ra?.base_price || 0;
    const pb = rb?.current_price || rb?.base_price || 0;
    if (sortMode === "price_desc") return pb - pa;
    if (sortMode === "qty_desc") return b.quantity - a.quantity;
    if (sortMode === "qty_asc") return a.quantity - b.quantity;
    if (sortMode === "tier_desc") return (TIER_ORDER[rb?.tier] ?? 0) - (TIER_ORDER[ra?.tier] ?? 0);
    return pa - pb; // price_asc default
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pagedItems = sortedItems.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Sort resources by price for market display
  const sortedResources = [...resources].sort((a, b) => (a.current_price || a.base_price || 0) - (b.current_price || b.base_price || 0));

  return (
    <GameLayout title="Inventory">
      <div className="space-y-6" data-testid="inventory-page">
        {sortedItems.length === 0 ? (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-12 text-center">
            <Package className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#94A3B8]">No resources yet. Start digging to collect!</p>
          </div>
        ) : (
          <>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-3 flex items-center gap-3 flex-wrap">
              <select value={sortMode} onChange={(e) => { setSortMode(e.target.value); setPage(1); }} data-testid="inventory-sort"
                className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="qty_desc">Quantity: High → Low</option>
                <option value="qty_asc">Quantity: Low → High</option>
                <option value="tier_desc">Rarity: Highest first</option>
              </select>
              <span className="text-[10px] text-[#64748B] ml-auto">{sortedItems.length} resource type(s)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagedItems.map((item) => {
              const res = getResource(item.resource_name);
              const tier = res?.tier || "common";
              const color = TIER_COLORS[tier];
              const price = res?.current_price || res?.base_price || 0;
              const qty = sellQty[item.resource_name] || "";
              const qtyNum = parseInt(qty) || 0;
              const fee = 0.05;
              const earnings = qtyNum > 0 ? (price * qtyNum * (1 - fee)).toFixed(1) : "0";

              return (
                <div key={item.resource_name} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 hover:border-opacity-100 transition-all" data-testid={`inv-item-${item.resource_name.toLowerCase()}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-bold text-[#F8FAFC]">{item.resource_name}</h3>
                      <span className="text-xs uppercase tracking-widest font-mono-game" style={{ color }}>{tier}</span>
                    </div>
                    <span className="font-mono-game text-lg font-bold text-[#F8FAFC]" data-testid={`inv-qty-${item.resource_name.toLowerCase()}`}>x{item.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-3 h-3 text-[#FFD600]" />
                    <span className="font-mono-game text-sm text-[#FFD600]">{price} coins/ea</span>
                  </div>

                  {/* Sell amount input */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input type="number" min="1" max={item.quantity} value={qty} placeholder="Amount"
                        onChange={(e) => setSellQty((p) => ({ ...p, [item.resource_name]: e.target.value }))}
                        className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-9 text-sm font-mono-game flex-1"
                        data-testid={`sell-qty-${item.resource_name.toLowerCase()}`} />
                    </div>

                    {/* Quick buttons */}
                    <div className="flex gap-1.5">
                      <button onClick={() => setSellQty((p) => ({ ...p, [item.resource_name]: String(item.quantity) }))}
                        className="flex-1 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10"
                        data-testid={`sell-all-${item.resource_name.toLowerCase()}`}>
                        All ({item.quantity})
                      </button>
                      <button onClick={() => setSellQty((p) => ({ ...p, [item.resource_name]: String(Math.ceil(item.quantity / 2)) }))}
                        className="flex-1 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] border border-[#2A3241] rounded-lg hover:bg-[#1C212E]"
                        data-testid={`sell-half-${item.resource_name.toLowerCase()}`}>
                        Half
                      </button>
                      <button onClick={() => setSellQty((p) => ({ ...p, [item.resource_name]: "1" }))}
                        className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] border border-[#2A3241] rounded-lg hover:bg-[#1C212E]">
                        1
                      </button>
                    </div>

                    {/* Sell button with earnings preview */}
                    <button onClick={() => sell(item.resource_name)} disabled={loading === item.resource_name || qtyNum <= 0}
                      data-testid={`sell-${item.resource_name.toLowerCase()}`}
                      className="w-full py-2.5 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-40 transition-all">
                      {loading === item.resource_name ? "Selling..." : qtyNum > 0 ? `Sell ${qtyNum} → ${earnings}c (5% fee)` : "Enter amount"}
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
            <Paginator page={safePage} totalPages={totalPages} total={sortedItems.length} onPageChange={setPage} testIdPrefix="inventory-pager" />
          </>
        )}

        {/* Market Prices — sorted least to most */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
          <h3 className="text-lg font-bold text-[#F8FAFC] mb-4">Market Prices</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {sortedResources.map((r) => (
              <div key={r.name} className="bg-[#0B0E14] rounded-lg p-3 text-center">
                <p className="text-xs uppercase tracking-widest font-mono-game mb-1" style={{ color: TIER_COLORS[r.tier] }}>{r.tier}</p>
                <p className="text-sm font-medium text-[#F8FAFC]">{r.name}</p>
                <p className="font-mono-game text-base font-bold text-[#FFD600] mt-1">{r.current_price || r.base_price}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </GameLayout>
  );
}
